// Executive Brain — proactive messaging + learning + history (pure logic).
//
// The CEO's three attention levels, the learning-aware responses, the append-only
// executive history, and the conversation scenario matcher live here as pure,
// dependency-free functions so they are unit-testable offline (node --test) and so
// the interaction pattern is correct now and bindable to real sources later.
//
// NOTHING here fabricates data. The demo events/answers themselves are placeholders
// that live in mock/mockData.js; this module only defines how they BEHAVE.

// Attention-level presentation metadata (restrained, semantic colors).
export const ATTENTION = {
  observation: { name: "Observation", rgb: "51,224,198", cssVar: "--teal",   title: "Cipher noticed something" },
  suggestion:  { name: "Suggestion",  rgb: "230,171,63", cssVar: "--amber",  title: "Cipher recommends a review" },
  critical:    { name: "Critical",    rgb: "242,140,64", cssVar: "--orange", title: "Cipher needs your attention" },
};

// The distinct behavior each level produces on the surface.
//  - observation → slim indicator, light illumination, does NOT interrupt.
//  - suggestion  → compact card auto-surfaces, illuminates, does NOT interrupt.
//  - critical    → auto-expands the conversation, stronger reasoning, INTERRUPTS.
export function surfaceBehavior(level) {
  switch (level) {
    case "observation": return { surface: "indicator", illuminate: "glance",        interrupts: false, autoExpand: false };
    case "suggestion":  return { surface: "card",      illuminate: "reason",        interrupts: false, autoExpand: false };
    case "critical":    return { surface: "thread",    illuminate: "reason-strong", interrupts: true,  autoExpand: true  };
    default:            return { surface: "card",      illuminate: "reason",        interrupts: false, autoExpand: false };
  }
}

// Learning-aware responses. `kind` separates a temporary dismissal from permanent
// feedback so Cipher can eventually learn from each differently.
export const LEARNING = {
  respond:      { label: "Respond",         kind: "engage",    status: "answered" },
  remind_later: { label: "Remind me later", kind: "temporary", status: "snoozed" },
  not_relevant: { label: "Not relevant",    kind: "permanent", status: "not_relevant" },
};

export const STATUS_LABEL = {
  surfaced: "Surfaced", answered: "Responded", snoozed: "Reminder set",
  not_relevant: "Marked not relevant", expanded: "Auto-expanded",
};
export function statusLabel(s) { return STATUS_LABEL[s] || s; }

// Conversation scenario matcher — keyword match, DEFAULT fallback. Never throws.
export function pickScenario(query, scenarios, def) {
  const s = String(query || "").toLowerCase();
  for (const sc of scenarios || []) {
    if ((sc.match || []).some((k) => s.includes(k))) return sc;
  }
  return def;
}

// ── Executive history (permanent, append-only). Pure functions returning a NEW
// array — a proactive event, once surfaced, is never removed. Learning actions
// update the record's status and append to its log; dismissal does NOT delete it.
// Shaped to persist into Decisions / Memory / Executive Timeline once wired. ──
export function recordSurface(history, ev, ts) {
  const list = history || [];
  if (list.some((r) => r.eid === ev.id)) return list; // idempotent per event
  const rec = { eid: ev.id, level: ev.level, msg: ev.msg, involved: ev.involved || [], status: "surfaced", ts: ts || "", log: ["surfaced"] };
  return [rec, ...list];
}

export function recordAction(history, eid, status) {
  return (history || []).map((r) => (r.eid === eid ? { ...r, status, log: [...r.log, status] } : r));
}

// Convenience: apply a learning action key ('respond'|'remind_later'|'not_relevant').
export function applyLearning(history, eid, actionKey) {
  const a = LEARNING[actionKey];
  return a ? recordAction(history, eid, a.status) : history;
}
